import { Fragment, h } from 'preact';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'preact/hooks';

import { getLabel } from '../../../../labels/labelling-service';

import { formatDuration } from './helpers/format-duration';
import { checkStatus } from './helpers/check-status';
import { getAudioData } from './helpers/get-audio-data';
import { AUDIO_PLAYER_STATUS } from './helpers/audio-player-status';
import Audio from './audio-component';
import PlayPause from './play-pause';
import Progress from './progress-component';

const LISTEN_TEXT = getLabel('audio.listen.text');
const INACTIVE_TEXT = getLabel('audio.player.not.active');

const KEY_ENTER = 'Enter';
const KEY_SPACE = ' ';
const KEY_ARROW_RIGHT = 'ArrowRight';
const KEY_ARROW_LEFT = 'ArrowLeft';
const SEEK_STEP = 5;

function AudioPlayer({ src, onReady, onPlay, onPause, onProgress, onComplete, onSeek }) {
  const audioRef = useRef();
  const listeners = {};
  const [progress, setProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [status, setStatus] = useState(AUDIO_PLAYER_STATUS.idle);

  const { isStatusIdle, isStatusActive, isStatusInactive } = checkStatus(status);

  const storeListener = (eventName, callbackRef) => {
    listeners[eventName] = callbackRef;
  };

  const mapDomEvents = (config) => {
    Object.keys(config).forEach((eventName) => {
      const callback = config[eventName];
      if (callback) {
        const callbackRef = () => callback(getAudioData(audioRef.current));
        audioRef.current.addEventListener(eventName, callbackRef);
        storeListener(eventName, callbackRef);
      }
    });
  };

  const removeAllListeners = () => {
    if (audioRef.current) {
      Object.keys(listeners).forEach((eventName) => {
        audioRef.current.removeEventListener(eventName, listeners[eventName]);
      });
    }
  };

  const handlePlayPause = () => {
    audioRef.current[isPlaying ? 'pause' : 'play']();
  };

  const activatePlayer = () => {
    if (isStatusIdle) {
      handlePlayPause();
    }
  };

  const updateProgress = (audioData) => {
    setProgress(audioData.progressPercentage);
    onProgress?.(audioData);
  };

  const updatePlay = (audioData) => {
    if (isStatusIdle) {
      setStatus(AUDIO_PLAYER_STATUS.active);
    }
    setIsPlaying(true);
    onPlay?.(audioData);
  };

  const updatePause = (audioData) => {
    setIsPlaying(false);
    onPause?.(audioData);
  };

  const handleProgressClick = (seekPercentage) => {
    const seekPosition = (audioRef.current.duration / 100) * seekPercentage;
    audioRef.current.currentTime = seekPosition;
  };

  const handleKeyDown = (event) => {
    if (event.key === KEY_ENTER || event.key ===  KEY_SPACE) {
      event.preventDefault();
      handlePlayPause();
    }
    if (status === AUDIO_PLAYER_STATUS.active && event.key === KEY_ARROW_RIGHT) {
      audioRef.current.currentTime += SEEK_STEP;
    }
    if (status === AUDIO_PLAYER_STATUS.active && event.key === KEY_ARROW_LEFT) {
      audioRef.current.currentTime -= SEEK_STEP;
    }
  };

  useEffect(() => {
    mapDomEvents({
      play: updatePlay,
      pause: updatePause,
      timeupdate: updateProgress,
      seeked: onSeek,
      ended: onComplete,
      loadeddata: onReady,
    });
    return removeAllListeners;
  }, []);

  useEffect(() => {
    const pauseAudio = () => {
      audioRef.current.pause();
      setIsPlaying(false);
      onPause?.(getAudioData(audioRef.current));
    };
    document.addEventListener('tts-pause', pauseAudio);
    return () => document.removeEventListener('tts-pause', pauseAudio);
  }, []);

  return (
    <AudioPlayer.Wrapper onClick={activatePlayer} onKeyDown={handleKeyDown} status={status} src={src}>
      <Audio src={src} ref={audioRef} />
      {(isStatusIdle || isStatusActive) && (
        <AudioPlayer.PlayPause>
          <PlayPause onClick={handlePlayPause} isPlaying={isPlaying} />
        </AudioPlayer.PlayPause>
      )}
      {isStatusIdle && <AudioPlayer.Message align="left">{LISTEN_TEXT}</AudioPlayer.Message>}
      {isStatusActive && (
        <Fragment>
          <AudioPlayer.Progress>
            <Progress percentage={progress} onClick={handleProgressClick} />
          </AudioPlayer.Progress>
          <AudioPlayer.Duration>{formatDuration(audioRef.current.currentTime)}</AudioPlayer.Duration>
        </Fragment>
      )}
      {isStatusInactive && <AudioPlayer.Message>{INACTIVE_TEXT}</AudioPlayer.Message>}
    </AudioPlayer.Wrapper>
  );
}

AudioPlayer.propTypes = {
  src: PropTypes.string,
  onReady: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onComplete: PropTypes.func,
  onSeek: PropTypes.func,
  onProgress: PropTypes.func,
};

AudioPlayer.Wrapper = ({ children, onClick, onKeyDown, status, src }) => (
  <div
    className={src ? 'c-audio-player' : 'c-audio-player--error'}
    onClick={onClick}
    onKeyDown={onKeyDown}
    data-state={status}
    tabIndex={0}
    role="application"
    aria-label={LISTEN_TEXT}
    aria-live="off">
    {children}
  </div>
);

AudioPlayer.Wrapper.propTypes = {
  children: PropTypes.any,
  onClick: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  src: PropTypes.string,
};

/* eslint-disable react/prop-types */
AudioPlayer.PlayPause = ({ children }) => <button className="c-audio-player__play-pause">{children}</button>;

AudioPlayer.Progress = ({ children }) => <div className="c-audio-player__progress">{children}</div>;

AudioPlayer.Duration = ({ children }) => <div className="c-audio-player__time">{children}</div>;

AudioPlayer.Message = ({ children, align }) => (
  <div className={`c-audio-player__message ${align && `c-audio-player__message--${align}`}`}>{children}</div>
);

export default AudioPlayer;
